import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { ApiError, PORTAL_ADMIN_REQUIRED_CODE, apiFetch } from '../lib/api'
import type { PortalAccessGrant, PortalRole } from '../lib/types'

export const SETTINGS_ACCESS_KEY = ['settings-access'] as const
const BASE = '/api/v1/settings/access'

const isAdminRequired = (err: unknown) =>
  err instanceof ApiError && err.status === 403 && err.code === PORTAL_ADMIN_REQUIRED_CODE

/** Starts a refetch or session re-read nothing waits for (a write's result
 * does not hold on it). Neither rejects — invalidateQueries keeps refetch
 * errors in the query, refreshSession catches its own — so the catch only
 * marks the promise as handled. */
const inBackground = (pending: Promise<unknown>) => {
  pending.catch(() => undefined)
}

/** Settings › Access's server state (MS-997 D7): the account's grants, and
 * the grant, role change and revoke behind the card. Each
 * write updates the cached list as soon as it lands, then refetches; none
 * toasts (the prototype's Settings script calls `toast()` nowhere) — the
 * page's Save toasts once for a whole batch of role changes
 * (hooks/usePortalRoleDrafts.ts, ARCHITECTURE_DECISIONS.md §64).
 *
 * The errors every write shares are handled here; the card shows the rest
 * where the plan places them (the Add modal, or inline under the table):
 * - 403 `auth.portal_admin_required` (the caller was demoted meanwhile): the
 *   session is re-read, and the route guard sends a Controller Home;
 * - 403 `auth.portal_access_denied` (revoked): lib/api.ts already routed to
 *   Access denied (D10);
 * - 404 (the row is gone): the list is refetched.
 * A change to the signed-in Admin's own row re-reads the session too, so a
 * self-demotion takes the Settings item away at once (CF edge 15). */
export function usePortalAccess() {
  const queryClient = useQueryClient()
  const { userEmail, refreshSession } = useAuth()
  const ownEmail = userEmail?.toLowerCase() ?? null

  const grants = useQuery({
    queryKey: SETTINGS_ACCESS_KEY,
    queryFn: () => apiFetch<PortalAccessGrant[]>(BASE),
  })

  const { error: loadError } = grants
  useEffect(() => {
    if (isAdminRequired(loadError)) inBackground(refreshSession())
  }, [loadError, refreshSession])

  const setRows = (update: (rows: PortalAccessGrant[]) => PortalAccessGrant[]) =>
    queryClient.setQueryData<PortalAccessGrant[]>(SETTINGS_ACCESS_KEY, (rows) => (rows ? update(rows) : rows))
  const refetch = () => queryClient.invalidateQueries({ queryKey: SETTINGS_ACCESS_KEY })
  const onError = (err: unknown) => {
    if (isAdminRequired(err)) inBackground(refreshSession())
    else if (err instanceof ApiError && err.status === 404) inBackground(refetch())
  }
  const afterOwnChange = (email: string) => {
    if (ownEmail !== null && email.toLowerCase() === ownEmail) inBackground(refreshSession())
  }

  const grant = useMutation({
    mutationFn: (input: { email: string; role: PortalRole }) =>
      apiFetch<PortalAccessGrant>(BASE, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (created) => {
      setRows((rows) => [...rows.filter((r) => r.id !== created.id), created])
      inBackground(refetch())
    },
    onError,
  })

  // `deferRefetch`: a batch (Save's run of PATCHes) skips the per-row refetch
  // and calls `refetchGrants` once when the run ends.
  const changeRole = useMutation({
    mutationFn: (input: { grant: PortalAccessGrant; role: PortalRole; deferRefetch?: boolean }) =>
      apiFetch<PortalAccessGrant>(`${BASE}/${encodeURIComponent(input.grant.id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: input.role }),
      }),
    onSuccess: (updated, input) => {
      setRows((rows) => rows.map((r) => (r.id === updated.id ? updated : r)))
      if (!input.deferRefetch) inBackground(refetch())
      afterOwnChange(updated.email)
    },
    onError,
  })

  const revoke = useMutation({
    mutationFn: (target: PortalAccessGrant) =>
      apiFetch<void>(`${BASE}/${encodeURIComponent(target.id)}`, { method: 'DELETE' }),
    onSuccess: (_, target) => {
      setRows((rows) => rows.filter((r) => r.id !== target.id))
      inBackground(refetch())
      afterOwnChange(target.email)
    },
    onError,
  })

  const refetchGrants = () => inBackground(refetch())

  return { grants, grant, changeRole, revoke, refetchGrants }
}
